import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import * as aiService from 'services/aiService'
import type { AiPlanBody, AiPlanProblem, AiPlanView, AiRouteContext, AiSessionSummary, AiToolStatus } from 'services/aiService'

/**
 * Ask MDT's state (MYD-625): the user's conversations, the one open now, and
 * the answer streaming into it.
 *
 * A message is sent with `sendAiMessage`. It adds the user's message and an
 * empty assistant message at once, then fills the assistant message as the
 * stream arrives (`aiStreamText`, `aiStreamTool`). To stop it, call `.abort()`
 * on the promise the dispatch returns, e.g. when the panel closes: the fetch is
 * cancelled, nothing more is dispatched, and the half-written answer is marked
 * stopped rather than failed.
 *
 * Errors are stored as the sentence to show (`friendlyAiError`), with the code
 * and the seconds until a limit resets, so the overlay never shows a status
 * line and can count down a rate limit.
 *
 * Plans (MYD-620, MYD-623): an answer that proposes changes carries a plan,
 * kept in `plans` by id and shown as a card under its message. The card saves
 * edits with `saveAiPlan` and runs the plan with `executeAiPlan`; the server
 * answers both with the plan as it now is, which replaces the stored one.
 */

const SLICE_NAME = 'ai'

export interface AiToolActivity {
	id: string
	name: string
	status: AiToolStatus
}

export interface AiChatMessage {
	id: string
	role: 'user' | 'assistant'
	text: string
	/** Assistant messages only: lookups made while answering. */
	tools: AiToolActivity[]
	status: 'streaming' | 'done' | 'stopped' | 'error'
	/** The plan this answer proposed, if it proposed one. */
	planId?: string
}

export interface AiError {
	message: string
	code: string | null
	retryAfter: number | null
}

/** What happened to the last save or run of a plan, for the card. */
export interface AiPlanActivity {
	saving: boolean
	executing: boolean
	undoing?: boolean
	/** The plan's status before a run started, restored if the run is refused. */
	statusBefore?: AiPlanView['status']
	/** The last refusal, in words, with the server's per-step problems. */
	error: { message: string; problems: AiPlanProblem[] } | null
}

export interface AiState {
	sessions: AiSessionSummary[]
	sessionsLoaded: boolean
	currentSessionId: string | null
	messages: AiChatMessage[]
	/** `sending` while an answer streams; `loading` while a conversation opens. */
	status: 'idle' | 'loading' | 'sending'
	error: AiError | null
	plans: Record<string, AiPlanView>
	planActivity: Record<string, AiPlanActivity>
}

export const initialState: AiState = {
	sessions: [],
	sessionsLoaded: false,
	currentSessionId: null,
	messages: [],
	status: 'idle',
	error: null,
	plans: {},
	planActivity: {},
}

const asError = (error: unknown): AiError => ({
	message: aiService.friendlyAiError(error),
	code: error instanceof aiService.AiRequestError ? error.code : null,
	retryAfter: error instanceof aiService.AiRequestError ? error.retryAfter : null,
})

// =============================================================================
// Thunks
// =============================================================================

export const fetchAiSessions = createAsyncThunk<AiSessionSummary[], void, { rejectValue: AiError }>(`${SLICE_NAME}/fetchSessions`, async (_, { rejectWithValue }) => {
	try {
		return await aiService.listSessions()
	} catch (error) {
		return rejectWithValue(asError(error))
	}
})

/** Open one of the user's conversations. */
export const openAiSession = createAsyncThunk<aiService.AiSession, string, { rejectValue: AiError }>(`${SLICE_NAME}/openSession`, async (id, { rejectWithValue }) => {
	try {
		return await aiService.getSession(id)
	} catch (error) {
		return rejectWithValue(asError(error))
	}
})

/**
 * Ask. Starts a conversation first when none is open. The answer arrives
 * through `aiStreamText` and `aiStreamTool` while this is pending; the
 * assistant message's id is this request's id.
 */
export const sendAiMessage = createAsyncThunk<
	{ sessionId: string; stopReason: string | null },
	{ text: string; routeContext?: AiRouteContext | null },
	{ state: { ai: AiState }; rejectValue: AiError }
>(`${SLICE_NAME}/sendMessage`, async ({ text, routeContext }, { dispatch, getState, signal, requestId, rejectWithValue }) => {
	try {
		let sessionId = getState().ai.currentSessionId
		if (!sessionId) {
			const session = await aiService.createSession(routeContext ?? null)
			dispatch(aiSessionStarted(session))
			sessionId = session.id
		}

		let stopReason: string | null = null
		for await (const event of aiService.sendMessage(sessionId, { text, routeContext }, { signal })) {
			// Aborted between events: dispatch nothing more.
			if (signal.aborted) break
			if (event.type === 'text') dispatch(aiStreamText({ messageId: requestId, delta: event.delta }))
			else if (event.type === 'tool') dispatch(aiStreamTool({ messageId: requestId, tool: { id: event.id, name: event.name, status: event.status } }))
			else if (event.type === 'plan') {
				const { type: _type, ...plan } = event
				dispatch(aiStreamPlan({ messageId: requestId, plan }))
			} else if (event.type === 'error') return rejectWithValue({ message: aiService.friendlyAiError(new aiService.AiRequestError(event.message, { code: event.code })), code: event.code, retryAfter: null })
			else if (event.type === 'done') stopReason = event.stopReason
		}
		return { sessionId, stopReason }
	} catch (error) {
		return rejectWithValue(asError(error))
	}
})

/** A plan refusal, in words, with the server's per-step problems when it sent them. */
export interface AiPlanRejection {
	message: string
	code: string | null
	problems: AiPlanProblem[]
}

const planRejection = (error: unknown): AiPlanRejection => {
	const refusal = error instanceof aiService.AiRequestError ? error : null
	const problems: AiPlanProblem[] = refusal?.details?.problems ?? []
	// A preflight refusal names steps too: { step, code, message }.
	const preflight: AiPlanProblem[] = (refusal?.details?.errors ?? []).map((entry: any) => ({ step: entry.step ?? null, path: '', message: entry.message }))
	return {
		message: refusal ? planErrorMessage(refusal) : aiService.friendlyAiError(error),
		code: refusal?.code ?? null,
		problems: [...problems, ...preflight],
	}
}

/** What to say when a plan is refused: the server writes these for people. */
const planErrorMessage = (refusal: aiService.AiRequestError) => {
	if (refusal.status === 404) return 'That plan is no longer available.'
	if (refusal.status === 0) return 'Ask MDT could not be reached. Check your connection and try again.'
	if (refusal.status >= 500) return 'Something went wrong. Try again in a moment.'
	return refusal.message
}

/** Save the user's edits to a plan. The server re-validates and answers with the plan as it now is. */
export const saveAiPlan = createAsyncThunk<AiPlanView, { id: string; plan: AiPlanBody }, { rejectValue: AiPlanRejection }>(
	`${SLICE_NAME}/savePlan`,
	async ({ id, plan }, { rejectWithValue }) => {
		try {
			return await aiService.updatePlan(id, plan)
		} catch (error) {
			return rejectWithValue(planRejection(error))
		}
	},
)

/** Run a plan the user confirmed. */
export const executeAiPlan = createAsyncThunk<AiPlanView, string, { rejectValue: AiPlanRejection }>(`${SLICE_NAME}/executePlan`, async (id, { rejectWithValue }) => {
	try {
		return await aiService.executePlan(id)
	} catch (error) {
		return rejectWithValue(planRejection(error))
	}
})

/** Read a plan again, e.g. to see a run's progress. */
export const fetchAiPlan = createAsyncThunk<AiPlanView, string, { rejectValue: AiPlanRejection }>(`${SLICE_NAME}/fetchPlan`, async (id, { rejectWithValue }) => {
	try {
		return await aiService.getPlan(id)
	} catch (error) {
		return rejectWithValue(planRejection(error))
	}
})

/**
 * Undo a plan that ran (MYD-619). The answer is each step's outcome; the plan
 * is then read again, so the card shows it as the server now holds it.
 */
export const undoAiPlan = createAsyncThunk<AiPlanView, string, { rejectValue: AiPlanRejection }>(`${SLICE_NAME}/undoPlan`, async (id, { rejectWithValue }) => {
	try {
		await aiService.undoPlan(id)
		return await aiService.getPlan(id)
	} catch (error) {
		return rejectWithValue(planRejection(error))
	}
})

/** Statuses a run ends on: a late progress poll must not take a plan back from these. */
const FINISHED = new Set(['done', 'partial', 'failed', 'undoing', 'undone'])

// =============================================================================
// Slice
// =============================================================================

const assistantMessage = (state: AiState, id: string) => state.messages.find(message => message.id === id && message.role === 'assistant')

const activityOf = (state: AiState, id: string): AiPlanActivity =>
	(state.planActivity[id] ??= { saving: false, executing: false, error: null })

/**
 * Store a plan as the server sent it. An answer without `refs` (an execute
 * response has no session at hand) keeps the labels already known, and one
 * without a verdict keeps no stale gaps.
 */
const storePlan = (state: AiState, plan: AiPlanView) => {
	const known = state.plans[plan.id]
	state.plans[plan.id] = { ...plan, refs: plan.refs ?? known?.refs ?? {} }
}

const aiSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {
		/** A fresh conversation, e.g. "New chat". */
		aiNewConversation: state => {
			state.currentSessionId = null
			state.messages = []
			state.error = null
		},
		aiClearError: state => {
			state.error = null
		},
		aiSessionStarted: (state, action: PayloadAction<AiSessionSummary>) => {
			state.currentSessionId = action.payload.id
			state.sessions = [action.payload, ...state.sessions.filter(session => session.id !== action.payload.id)]
		},
		/** The answer proposed a plan: keep it, and hang its card on the message. */
		aiStreamPlan: (state, action: PayloadAction<{ messageId: string; plan: AiPlanView }>) => {
			storePlan(state, action.payload.plan)
			const message = assistantMessage(state, action.payload.messageId)
			if (message) message.planId = action.payload.plan.id
		},
		/** A plan proposed outside the chat (MYD-643, tasks from a note): keep it for its card. */
		aiPlanReceived: (state, action: PayloadAction<AiPlanView>) => {
			storePlan(state, action.payload)
		},
		aiClearPlanError: (state, action: PayloadAction<string>) => {
			activityOf(state, action.payload).error = null
		},
		aiStreamText: (state, action: PayloadAction<{ messageId: string; delta: string }>) => {
			const message = assistantMessage(state, action.payload.messageId)
			if (message) message.text += action.payload.delta
		},
		aiStreamTool: (state, action: PayloadAction<{ messageId: string; tool: AiToolActivity }>) => {
			const message = assistantMessage(state, action.payload.messageId)
			if (!message) return
			const existing = message.tools.find(tool => tool.id === action.payload.tool.id)
			if (existing) existing.status = action.payload.tool.status
			else message.tools.push(action.payload.tool)
		},
	},
	extraReducers: builder => {
		builder
			.addCase(fetchAiSessions.fulfilled, (state, action) => {
				state.sessions = action.payload
				state.sessionsLoaded = true
			})
			.addCase(fetchAiSessions.rejected, (state, action) => {
				state.error = action.payload ?? null
			})

			.addCase(openAiSession.pending, state => {
				state.status = 'loading'
				state.error = null
			})
			.addCase(openAiSession.fulfilled, (state, action) => {
				state.status = 'idle'
				state.currentSessionId = action.payload.id
				state.messages = action.payload.messages.map((entry, index) => ({
					id: `${action.payload.id}:${index}`,
					role: entry.role,
					text: entry.text,
					tools: [],
					status: 'done',
				}))
			})
			.addCase(openAiSession.rejected, (state, action) => {
				state.status = 'idle'
				state.error = action.payload ?? null
			})

			.addCase(sendAiMessage.pending, (state, action) => {
				state.status = 'sending'
				state.error = null
				const { requestId, arg } = action.meta
				state.messages.push(
					{ id: `${requestId}:user`, role: 'user', text: arg.text, tools: [], status: 'done' },
					{ id: requestId, role: 'assistant', text: '', tools: [], status: 'streaming' },
				)
			})
			.addCase(sendAiMessage.fulfilled, (state, action) => {
				state.status = 'idle'
				const message = assistantMessage(state, action.meta.requestId)
				if (message) message.status = 'done'
			})
			.addCase(sendAiMessage.rejected, (state, action) => {
				state.status = 'idle'
				const message = assistantMessage(state, action.meta.requestId)
				// Stopped by the user is not a failure: keep what arrived, no error.
				if (action.meta.aborted) {
					if (message) message.status = 'stopped'
					return
				}
				if (message) message.status = 'error'
				state.error = action.payload ?? { message: aiService.friendlyAiError(action.error), code: null, retryAfter: null }
			})

			// Plans.
			.addCase(saveAiPlan.pending, (state, action) => {
				const activity = activityOf(state, action.meta.arg.id)
				activity.saving = true
				activity.error = null
			})
			.addCase(saveAiPlan.fulfilled, (state, action) => {
				storePlan(state, action.payload)
				activityOf(state, action.payload.id).saving = false
			})
			.addCase(saveAiPlan.rejected, (state, action) => {
				const activity = activityOf(state, action.meta.arg.id)
				activity.saving = false
				activity.error = action.payload ? { message: action.payload.message, problems: action.payload.problems } : { message: 'Your change could not be saved.', problems: [] }
			})
			.addCase(executeAiPlan.pending, (state, action) => {
				const activity = activityOf(state, action.meta.arg)
				activity.executing = true
				activity.error = null
				const plan = state.plans[action.meta.arg]
				if (plan) {
					activity.statusBefore = plan.status
					plan.status = 'executing'
				}
			})
			.addCase(executeAiPlan.fulfilled, (state, action) => {
				storePlan(state, action.payload)
				activityOf(state, action.payload.id).executing = false
			})
			.addCase(executeAiPlan.rejected, (state, action) => {
				const activity = activityOf(state, action.meta.arg)
				activity.executing = false
				activity.error = action.payload ? { message: action.payload.message, problems: action.payload.problems } : { message: 'The plan could not run.', problems: [] }
				// Refused before anything ran: back to where it was, so it can be fixed.
				const plan = state.plans[action.meta.arg]
				if (plan?.status === 'executing' && activity.statusBefore) plan.status = activity.statusBefore
			})
			.addCase(fetchAiPlan.fulfilled, (state, action) => {
				// A poll answered after the run's own response: keep the finished plan.
				const known = state.plans[action.payload.id]
				if (known && FINISHED.has(known.status) && action.payload.status === 'executing') return
				storePlan(state, action.payload)
			})
			.addCase(undoAiPlan.pending, (state, action) => {
				const activity = activityOf(state, action.meta.arg)
				activity.undoing = true
				activity.error = null
			})
			.addCase(undoAiPlan.fulfilled, (state, action) => {
				storePlan(state, action.payload)
				activityOf(state, action.payload.id).undoing = false
			})
			.addCase(undoAiPlan.rejected, (state, action) => {
				const activity = activityOf(state, action.meta.arg)
				activity.undoing = false
				activity.error = action.payload ? { message: action.payload.message, problems: [] } : { message: 'The plan could not be undone.', problems: [] }
			})
	},
})

export const { aiNewConversation, aiClearError, aiClearPlanError, aiPlanReceived, aiSessionStarted, aiStreamPlan, aiStreamText, aiStreamTool } = aiSlice.actions

export default aiSlice
